import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Grid,
  Box,
  Typography,
  useTheme,
  Modal,
  TextField,
  TextareaAutosize,
  FormControl,
  FormHelperText,
  Button,
  Select,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import { useDeviceSize } from "../../hooks";
import {
  IDLE,
  MOBILE_SIZE_SCREEN,
  SMALL_MOBILE_SIZE_SCREEN,
} from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { fetchProjects } from "../../store/slice/projectHandler";
import {
  fetchTasks,
  Task,
  fetchDomains,
  Domain,
} from "../../store/slice/ProjectTasksSlice";
import { ProjectPayload, addProject } from "../../store/slice/ProjectSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const VisionStudioProject = (props: any) => {
  const dispatch: AppDispatch = useDispatch();
  const { status } = useSelector((state: RootState) => state.project);
  const { tasks, domains } = useSelector((state: RootState) => state.Tasks);
  const [tasksList, setTasksList] = useState<Task[]>([]);
  const [domainsList, setDomainsList] = useState<Domain[]>([]);
  const [isSubmit, setIsSubmit] = useState<Boolean>(false);
  const [active, setActive] = useState<Boolean>(false);
  const [isTrackingEnabled, setIsTrackingEnabled] = useState<boolean>(false);
  const [isTracking, setIsTracking] = useState<boolean>(false);
  const navigate = useNavigate();

  const [formFields, setFormFields] = useState<ProjectPayload>({
    id: "",
    name: "",
    description: "",
    taskTypeId: "0",
    domainTypeId: "0",
  });

  React.useEffect(() => {
    if (status === IDLE) {
      dispatch(fetchProjects());
    }
  }, [status, dispatch]);

  useEffect(() => {
    dispatch(fetchTasks());
    if (props.open) {
      dispatch(fetchDomains());
    }
  }, [props.open]);

  useEffect(() => {
    if (tasks.length > 0) {
      setTasksList(tasks);
    }
  }, [tasks]);

  useEffect(() => {
    if (domains.length > 0) {
      setDomainsList(domains);
    }
  }, [domains]);

  const handleNothing = () => {};

  const handleSubmit = async () => {
    setIsSubmit(true);
    if (
      formFields.taskTypeId !== "0" &&
      formFields.domainTypeId !== "0" &&
      formFields.name.length > 2
    ) {
      let tmpFormFields = formFields;
      const selectedTaskId = formFields.taskTypeId;
      const selectedTask = tasksList.find((task) => task.id === selectedTaskId);
      const similarEntries = tasksList?.filter((task) =>
        task.name.includes(selectedTask!.name)
      );

      if (isTracking) {
        const trackingTask = similarEntries.find((task) =>
          task.name.includes("Tracking")
        );

        if (trackingTask) {
          tmpFormFields.taskTypeId = trackingTask.id;
          if (selectedTask) {
            setFormFields({
              ...formFields,
              taskTypeId: selectedTask.id,
            });
          }
        }
      }

      try {
        setActive(true);
        const result = await dispatch(addProject(tmpFormFields))
          .unwrap()
          .then((result) => navigate(`../project/${result.id}`));
        toast.success(`Project "${tmpFormFields.name}" successfully added!`);
        dispatch(fetchProjects());
        setFormFields({
          id: "",
          name: "",
          description: "",
          taskTypeId: "0",
          domainTypeId: "0",
        });
        setIsTrackingEnabled(false);
        setIsTracking(false);
        props?.setOpen(false);
        setIsSubmit(false);
        setActive(false);
      } catch (error: any) {
        let errorMessage: string;

        if (typeof error === "string") {
          // If the error is a string, set the error message to the string.
          errorMessage = error;
        } else if (error && error.detail) {
          // If the error has a 'detail' property, use that.
          errorMessage = error.detail;
        } else {
          // Fallback for unexpected error formats.
          errorMessage = "An unexpected error occurred.";
        }

        toast.error(errorMessage);
        setActive(false);
        setIsSubmit(false);
      }
    } else {
      // toast.warn("Enter Form Fields");
    }
  };

  const filteredTasks = tasksList.filter(
    (task) => !task.name.includes("Tracking")
  );

  const handleTaskChange = async (e: SelectChangeEvent<string>) => {
    setFormFields({
      ...formFields,
      taskTypeId: e.target.value,
    });
    setIsTracking(false);

    const selectedTaskId = e.target.value;

    const selectedTask = tasksList.find((task) => task.id === selectedTaskId);
    const similarEntries = tasksList?.filter((task) =>
      task.name.includes(selectedTask!.name)
    );

    if (similarEntries.length > 1) {
      setIsTrackingEnabled(true);
    } else {
      setIsTrackingEnabled(false);
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIsTracking(true);
    } else {
      setIsTracking(false);
    }
  };

  return (
    <Modal
      open={props?.open}
      onClose={props?.handleClose}
      sx={{
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          p: 4,
        }}
      >
        <Typography
          id="modal-modal-title"
          variant="h6"
          component="h3"
          sx={{ marginBottom: "12px" }}
        >
          Project Creation
        </Typography>
        <Box>
          <FormControl
            sx={{ width: "100%", margin: "10px 0" }}
            variant="outlined"
          >
            <FormHelperText
              id="outlined-iteration-name-text"
              sx={{
                margin: "0",
                fontWeight: "500",
                fontSize: "16px",
                m: "5px 0",
                color: "#333333",
              }}
            >
              Enter Name{" "}
              <span style={{ color: "red", fontSize: "10px" }}>*</span>
            </FormHelperText>
            <TextField
              fullWidth
              id="fullWidth"
              placeholder="Enter Name here"
              size="small"
              value={formFields?.name}
              sx={{
                width: "100%",
                background: "#F5F6F9",
                border:
                  isSubmit &&
                  (formFields.name === "" || formFields?.name?.length < 3)
                    ? "1px solid red"
                    : "0",
                fontSize: "12px",
                borderRadius: "4px",
                "& .MuiOutlinedInput-root": {
                  "& input": {
                    color: "#666", // Target the input text color
                  },
                  border: "none",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
              }}
              onChange={(e) => {
                const value = e.target.value;
                // Trim leading spaces, limit to 50 characters, and prevent double spaces
                const sanitizedValue = value
                  .trimStart()
                  .slice(0, 50)
                  .replace(/\s{2,}/g, " ");
                setFormFields({ ...formFields, name: sanitizedValue });
              }}
            />

            {isSubmit && formFields?.name?.length < 3 ? (
              <span
                style={{ fontSize: "12px", color: "red", marginTop: "7px" }}
              >
                Minimum 3 characters
              </span>
            ) : (
              ""
            )}
          </FormControl>
          <FormControl
            sx={{ width: "100%", margin: "10px 0" }}
            variant="outlined"
          >
            <FormHelperText
              sx={{
                margin: "0",
                fontWeight: "500",
                fontSize: "16px",
                m: "5px 0",
                color: "#333333",
              }}
            >
              Task <span style={{ color: "red", fontSize: "10px" }}>*</span>
            </FormHelperText>

            <Select
              id="projectTypeId"
              labelId="dropdown-label"
              value={formFields?.taskTypeId || "0"}
              onChange={handleTaskChange}
              sx={{
                width: "100%",
                background: "#F5F6F9",
                border:
                  isSubmit && formFields.taskTypeId === "0"
                    ? "1px solid red"
                    : "0",
                fontSize: "12px",
                borderRadius: "4px",
                "& .MuiSelect-select": {
                  padding: "15px",
                  fontSize: "15px",
                  color: "#666",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    overflowY: "auto",
                  },
                },
              }}
            >
              <MenuItem value="0" disabled>
                Select Task
              </MenuItem>

              {/* Group tasks by last name */}
              {(() => {
                // Group tasks by last name
                const groupedTasks = filteredTasks.reduce<
                  Record<string, Task[]>
                >((groups, task) => {
                  const lastName = task.name.split(" ").pop() || "";
                  if (!groups[lastName]) {
                    groups[lastName] = [];
                  }
                  groups[lastName].push(task);
                  return groups;
                }, {});

                // Convert to array and render
                return Object.entries(groupedTasks).map(([lastName, tasks]) => [
                  // Group Header - using Fragment instead of div
                  <MenuItem
                    key={`header-${lastName}`}
                    disabled
                    sx={{
                      backgroundColor: "#f8f9fa",
                      fontWeight: "bold",
                      fontSize: "13px",
                      color: "#555",
                      pointerEvents: "none",
                      padding: "8px 16px",
                      borderBottom: "1px solid #e0e0e0",
                      "&.Mui-disabled": {
                        opacity: 1,
                      },
                    }}
                  >
                    {lastName} Tasks
                  </MenuItem>,

                  // Tasks in this group
                  ...tasks.map((task) => (
                    <MenuItem
                      key={task.id}
                      value={task.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 16px 10px 20px",
                        fontSize: "14px",
                        "&:hover": {
                          backgroundColor: "#f0f2f5",
                        },
                      }}
                      disabled={
                        task.name === "Pose Detections" ||
                        task.name === "Classification"
                      }
                    >
                      <span
                        style={{
                          height: "10px",
                          width: "10px",
                          borderRadius: "2px",
                          background: task.colour,
                          marginRight: "8px",
                          flexShrink: 0,
                        }}
                      />
                      {task.name}
                    </MenuItem>
                  )),
                ]);
              })()}
            </Select>
            {/* <Box>
              <FormControlLabel
                label="Enable Tracking"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "13px", // Set label font size here
                  },
                }}
                control={
                  <Checkbox
                    size="small"
                    sx={{ color: "black" }}
                    inputProps={{ "aria-label": "Enable Tracking" }}
                    disabled={!isTrackingEnabled}
                    checked={isTracking}
                    onChange={handleCheckboxChange}
                  />
                }
              />
            </Box> */}
            {isSubmit && formFields?.taskTypeId == "0" ? (
              <span
                style={{ fontSize: "12px", color: "red", marginTop: "7px" }}
              >
                Task is required
              </span>
            ) : (
              ""
            )}
          </FormControl>
          <FormControl
            sx={{ width: "100%", margin: "10px 0" }}
            variant="outlined"
          >
            <FormHelperText
              sx={{
                margin: "0",
                fontWeight: "500",
                fontSize: "16px",
                m: "5px 0",
                color: "#333333",
              }}
            >
              Domain <span style={{ color: "red", fontSize: "10px" }}>*</span>
            </FormHelperText>
            <Select
              id="projectDomain"
              labelId="dropdown-label"
              value={formFields?.domainTypeId || "0"} // Provide a default value
              onChange={(e) =>
                setFormFields({
                  ...formFields,
                  domainTypeId: e.target.value,
                })
              }
              sx={{
                width: "100%",
                background: "#F5F6F9",
                border:
                  isSubmit && formFields.domainTypeId == "0"
                    ? "1px solid red"
                    : "0",
                fontSize: "12px",
                borderRadius: "4px",
                "& .MuiSelect-select": {
                  padding: "15px",
                  fontSize: "15px",
                  color: "#666",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200, // Set your desired max height
                    overflowY: "auto", // Enable vertical scrolling
                  },
                },
              }}
            >
              <MenuItem value="0" disabled>
                Select Domain
              </MenuItem>
              {domainsList.length ? (
                domainsList.map((domian) => (
                  <MenuItem key={domian.id} value={domian.id}>
                    {domian.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No domains available</MenuItem>
              )}
            </Select>
            {isSubmit && formFields?.domainTypeId == "0" ? (
              <span
                style={{ fontSize: "12px", color: "red", marginTop: "7px" }}
              >
                Domain is required
              </span>
            ) : (
              ""
            )}
          </FormControl>
          <FormControl
            sx={{ width: "100%", margin: "10px 0" }}
            variant="outlined"
          >
            <FormHelperText
              sx={{
                margin: "0",
                fontWeight: "500",
                fontSize: "16px",
                m: "5px 0",
                color: "#333333",
              }}
            >
              Description{" "}
            </FormHelperText>
            <TextareaAutosize
              value={formFields?.description || ""}
              minRows={5}
              placeholder="Type here"
              style={{
                background: "#F5F6F9",
                border: "0",
                borderRadius: "4px",
                padding: "10px",
                outline: 0,
                fontSize: "15px",
                maxWidth: "97%",
                maxHeight: "100px",
                minHeight: "100px",
                minWidth: "96%",
              }}
              onChange={(e) => {
                const value = e.target.value;
                const other = value.trimStart().slice(0, 100);
                setFormFields({
                  ...formFields,
                  description: other,
                });
              }}
            />
          </FormControl>
        </Box>
        <Box
          sx={{
            justifyContent: "end",
            display: "flex",
            gap: "10px",
            marginTop: "10px",
          }}
        >
          <Button
            sx={{
              background: "white",
              color: "#0F6CBD",
              fontSize: "16px",
              border: "1px solid #0F6CBD",
              width: "130px",
              borderRadius: "0",
            }}
            onClick={(e) => {
              props?.handleClose();
              setFormFields({
                id: "",
                name: "",
                description: "",
                taskTypeId: "0",
                domainTypeId: "0",
              });
              setIsTrackingEnabled(false);
              setIsTracking(false);
              setIsSubmit(false);
            }}
          >
            Cancel
          </Button>
          <Button
            sx={{
              background: "#0F6CBD",
              color: "white",
              fontSize: "16px",
              border: "1px solid #0F6CBD",
              width: "130px",
              borderRadius: "0",
              "&:hover": {
                background: "white",
                color: "#0F6CBD",
              },
            }}
            onClick={
              formFields.taskTypeId !== "0" &&
              formFields.domainTypeId !== "0" &&
              formFields.name.length > 2 &&
              isSubmit &&
              active
                ? handleNothing
                : handleSubmit
            }
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default VisionStudioProject;
